// KAVRIS Mock Data — Prototype use only. Not approved KAVRIS content.

export type NodeStatus = 'Approved' | 'Working' | 'Draft' | 'Experimental' | 'Prototype';
export type NodeAuthority = 'Codex' | 'Atlas' | 'Foundation' | 'Product Framework' | 'Prototype';
export type NodeType =
  | 'Core System' |'Knowledge Layer' |'Navigation Layer' |'Product Framework' |'Enterprise Edition' |'Institutional Document' |'Governance Layer' |'Architecture Layer';

export interface KavrisNode {
  id: string;
  label: string;
  type: NodeType;
  shortDescription: string;
  description: string;
  status: NodeStatus;
  authority: NodeAuthority;
  version: string;
  parentId: string | null;
  relatedIds: string[];
  layer: string;
  position?: { x: number; y: number };
}

export interface CodexEntry {
  id: string;
  title: string;
  category: string;
  content: string;
  status: NodeStatus;
  authority: NodeAuthority;
  version: string;
  relatedAtlasNodes: string[];
  relatedCodexEntries: string[];
  frameworkRelationship: string;
  parentElement: string;
  isDemo: boolean;
  lastUpdated: string;
}

export interface FoundationDocument {
  id: string;
  title: string;
  type: string;
  excerpt: string;
  status: NodeStatus;
  version: string;
  isDemo: boolean;
}

export const kavrisNodes: KavrisNode[] = [
  {
    id: 'node-kavris-core',
    label: 'KAVRIS',
    type: 'Core System',
    shortDescription: 'Enterprise intelligence ecosystem and intelligent services architecture.',
    description: 'KAVRIS is an emerging enterprise intelligence ecosystem and intelligent services architecture. It provides a structured digital environment where knowledge, architecture, governance, navigation, and future intelligent capabilities converge.',
    status: 'Working',
    authority: 'Foundation',
    version: 'v0.1',
    parentId: null,
    relatedIds: ['node-codex', 'node-atlas', 'node-product-framework', 'node-foundation', 'node-pf001', 'node-knowledge'],
    layer: 'Core',
    position: { x: 50, y: 50 },
  },
  {
    id: 'node-codex',
    label: 'Codex',
    type: 'Knowledge Layer',
    shortDescription: 'The authoritative corpus of knowledge, principles, standards, governance, rules, and definitions.',
    description: 'KAVRIS Codex is the authoritative knowledge and governance layer. It contains principles, standards, governance frameworks, rules, definitions, and institutional logic that govern the entire KAVRIS ecosystem.',
    status: 'Working',
    authority: 'Codex',
    version: 'v0.1',
    parentId: 'node-kavris-core',
    relatedIds: ['node-kavris-core', 'node-atlas', 'node-product-framework', 'node-foundation'],
    layer: 'Knowledge',
    position: { x: 20, y: 25 },
  },
  {
    id: 'node-atlas',
    label: 'Atlas',
    type: 'Navigation Layer',
    shortDescription: 'The visual and navigational representation of the KAVRIS ecosystem.',
    description: 'KAVRIS Atlas provides the visual and navigational map of the entire ecosystem. It helps users understand architecture, relationships, layers, components, domains, connections, and product structures.',
    status: 'Working',
    authority: 'Atlas',
    version: 'v0.1',
    parentId: 'node-kavris-core',
    relatedIds: ['node-kavris-core', 'node-codex', 'node-product-framework'],
    layer: 'Architecture',
    position: { x: 80, y: 25 },
  },
  {
    id: 'node-product-framework',
    label: 'Product Framework',
    type: 'Product Framework',
    shortDescription: 'Structured architecture connecting KAVRIS concepts to products and implementations.',
    description: 'The KAVRIS Product Framework is the structured architecture that connects foundational KAVRIS concepts to products and implementations. It defines how KAVRIS manifests as enterprise-grade products.',
    status: 'Working',
    authority: 'Product Framework',
    version: 'v0.1',
    parentId: 'node-kavris-core',
    relatedIds: ['node-kavris-core', 'node-codex', 'node-atlas', 'node-pf001'],
    layer: 'Product',
    position: { x: 80, y: 75 },
  },
  {
    id: 'node-foundation',
    label: 'Foundation',
    type: 'Institutional Document',
    shortDescription: 'The executive foundation: Origin, Vision, Mission, Manifesto, Constitution, Charter, Product Identity.',
    description: 'The KAVRIS Executive Foundation contains the institutional bedrock of the ecosystem — Origin, Vision, Mission, Manifesto, Constitution, Charter, and Product Identity. These are foundational institutional elements rather than ordinary marketing documents.',
    status: 'Working',
    authority: 'Foundation',
    version: 'v0.1',
    parentId: 'node-kavris-core',
    relatedIds: ['node-kavris-core', 'node-codex', 'node-atlas', 'node-product-framework'],
    layer: 'Foundation',
    position: { x: 50, y: 15 },
  },
  {
    id: 'node-pf001',
    label: 'PF-001 Enterprise',
    type: 'Enterprise Edition',
    shortDescription: 'The first defined KAVRIS Product Framework item — Enterprise Edition.',
    description: 'PF-001 Enterprise Edition is the first defined item within the KAVRIS Product Framework. It represents the enterprise-grade implementation of the KAVRIS ecosystem, governed by Codex and navigated via Atlas.',
    status: 'Draft',
    authority: 'Product Framework',
    version: 'v0.1',
    parentId: 'node-product-framework',
    relatedIds: ['node-product-framework', 'node-codex', 'node-atlas', 'node-foundation'],
    layer: 'Enterprise',
    position: { x: 20, y: 75 },
  },
  {
    id: 'node-knowledge',
    label: 'Knowledge',
    type: 'Knowledge Layer',
    shortDescription: 'The navigable knowledge corpus of the KAVRIS ecosystem.',
    description: 'The KAVRIS Knowledge layer provides structured, navigable access to the ecosystem\'s knowledge corpus — including concepts, definitions, relationships, and contextual intelligence.',
    status: 'Experimental',
    authority: 'Codex',
    version: 'v0.1',
    parentId: 'node-kavris-core',
    relatedIds: ['node-kavris-core', 'node-codex', 'node-atlas'],
    layer: 'Knowledge',
    position: { x: 50, y: 85 },
  },
];

export const atlasLayers = [
  {
    id: 'layer-foundation',
    label: 'Foundation',
    description: 'Origin, Vision, Mission, Manifesto, Constitution, Charter, Product Identity',
    depth: 1,
    nodeIds: ['node-foundation'],
    color: 'var(--layer-foundation)',
    status: 'Working' as NodeStatus,
  },
  {
    id: 'layer-governance',
    label: 'Governance',
    description: 'Principles, Standards, Rules, Definitions, Institutional Logic',
    depth: 2,
    nodeIds: ['node-codex'],
    color: 'var(--layer-governance)',
    status: 'Working' as NodeStatus,
  },
  {
    id: 'layer-knowledge',
    label: 'Knowledge',
    description: 'Authoritative corpus, Codex entries, Knowledge records',
    depth: 3,
    nodeIds: ['node-codex', 'node-knowledge'],
    color: 'var(--layer-knowledge)',
    status: 'Working' as NodeStatus,
  },
  {
    id: 'layer-architecture',
    label: 'Architecture',
    description: 'Ecosystem map, Relationships, Structural visualization',
    depth: 4,
    nodeIds: ['node-atlas'],
    color: 'var(--layer-architecture)',
    status: 'Working' as NodeStatus,
  },
  {
    id: 'layer-product',
    label: 'Product',
    description: 'Product Framework, Implementation architecture',
    depth: 5,
    nodeIds: ['node-product-framework'],
    color: 'var(--layer-product)',
    status: 'Draft' as NodeStatus,
  },
  {
    id: 'layer-enterprise',
    label: 'Enterprise Implementation',
    description: 'PF-001 Enterprise Edition and future implementations',
    depth: 6,
    nodeIds: ['node-pf001'],
    color: 'var(--layer-enterprise)',
    status: 'Draft' as NodeStatus,
  },
];

export const codexCategories = [
  'Principles',
  'Governance',
  'Standards',
  'Definitions',
  'Architecture',
  'Frameworks',
  'Foundation',
];

export const codexEntries: CodexEntry[] = [
  {
    id: 'codex-001',
    title: 'Principle of Knowledge Authority',
    category: 'Principles',
    content: `[DEMO — Prototype visualization only]

The Principle of Knowledge Authority establishes that every element within the KAVRIS ecosystem must carry an explicit authority designation. Information without authority attribution is unstructured and therefore outside the governed KAVRIS knowledge architecture.

Authority flows from Foundation through Codex, is mapped by Atlas, and is implemented through the Product Framework.

**Governance Note:** This entry is a prototype placeholder. Official content is reserved for approved KAVRIS source material.`,
    status: 'Working',
    authority: 'Codex',
    version: 'v0.1',
    relatedAtlasNodes: ['node-codex', 'node-foundation'],
    relatedCodexEntries: ['codex-002', 'codex-005'],
    frameworkRelationship: 'Governs all Product Framework implementations',
    parentElement: 'KAVRIS Core Principles',
    isDemo: true,
    lastUpdated: '2026-08-01',
  },
  {
    id: 'codex-002',
    title: 'Governance Structure Definition',
    category: 'Governance',
    content: `[DEMO — Prototype visualization only]

The KAVRIS Governance Structure defines the hierarchical and relational authority model across the ecosystem. Governance is not a separate function — it is embedded in every layer of the KAVRIS architecture.

Governance elements include: authority designation, status lifecycle, version control, and relationship mapping.

**Governance Note:** This entry is a prototype placeholder. Official content is reserved for approved KAVRIS source material.`,
    status: 'Working',
    authority: 'Codex',
    version: 'v0.1',
    relatedAtlasNodes: ['node-codex', 'node-foundation'],
    relatedCodexEntries: ['codex-001', 'codex-003'],
    frameworkRelationship: 'Defines governance requirements for all PF implementations',
    parentElement: 'KAVRIS Governance',
    isDemo: true,
    lastUpdated: '2026-08-01',
  },
  {
    id: 'codex-003',
    title: 'Ecosystem Architecture Standard',
    category: 'Standards',
    content: `[DEMO — Prototype visualization only]

The Ecosystem Architecture Standard defines how KAVRIS components are structured, related, and represented within the Atlas. All architectural elements must conform to defined node types, layer assignments, and relationship classifications.

**Governance Note:** This entry is a prototype placeholder. Official content is reserved for approved KAVRIS source material.`,
    status: 'Draft',
    authority: 'Codex',
    version: 'v0.1-draft',
    relatedAtlasNodes: ['node-atlas', 'node-codex'],
    relatedCodexEntries: ['codex-002', 'codex-004'],
    frameworkRelationship: 'Defines structural requirements for Atlas representations',
    parentElement: 'KAVRIS Standards',
    isDemo: true,
    lastUpdated: '2026-07-28',
  },
  {
    id: 'codex-004',
    title: 'Node Definition Standard',
    category: 'Definitions',
    content: `[DEMO — Prototype visualization only]

A KAVRIS Node is any discrete element within the ecosystem that carries: a unique identifier, a type classification, a status designation, an authority attribution, a layer assignment, and at least one defined relationship to another node.

Nodes without complete metadata are considered unresolved and cannot be included in the governed Atlas.

**Governance Note:** This entry is a prototype placeholder. Official content is reserved for approved KAVRIS source material.`,
    status: 'Working',
    authority: 'Codex',
    version: 'v0.1',
    relatedAtlasNodes: ['node-atlas', 'node-codex'],
    relatedCodexEntries: ['codex-003'],
    frameworkRelationship: 'Applies to all node types within the Product Framework',
    parentElement: 'KAVRIS Definitions',
    isDemo: true,
    lastUpdated: '2026-08-01',
  },
  {
    id: 'codex-005',
    title: 'KAVRIS Architecture Layers',
    category: 'Architecture',
    content: `[DEMO — Prototype visualization only]

The KAVRIS Architecture is organized into conceptual layers that represent the depth and authority of information within the ecosystem. Layers are navigational and conceptual — they are not organizational units or product tiers.

Layers (prototype naming, not approved taxonomy): Foundation → Governance → Knowledge → Architecture → Product → Enterprise Implementation.

**Governance Note:** This entry is a prototype placeholder. Layer naming is experimental and subject to change.`,
    status: 'Experimental',
    authority: 'Atlas',
    version: 'v0.1-experimental',
    relatedAtlasNodes: ['node-atlas', 'node-kavris-core'],
    relatedCodexEntries: ['codex-003', 'codex-004'],
    frameworkRelationship: 'Informs the Product Framework layer assignment model',
    parentElement: 'KAVRIS Architecture',
    isDemo: true,
    lastUpdated: '2026-07-25',
  },
  {
    id: 'codex-006',
    title: 'Product Framework Principles',
    category: 'Frameworks',
    content: `[DEMO — Prototype visualization only]

The KAVRIS Product Framework operates on the principle that products are implementations of the ecosystem's foundational architecture — not independent systems. Every Product Framework item must derive its structure from Codex, its navigation from Atlas, and its authority from Foundation.

**Governance Note:** This entry is a prototype placeholder. Official content is reserved for approved KAVRIS source material.`,
    status: 'Draft',
    authority: 'Product Framework',
    version: 'v0.1-draft',
    relatedAtlasNodes: ['node-product-framework', 'node-codex'],
    relatedCodexEntries: ['codex-001', 'codex-002'],
    frameworkRelationship: 'Directly governs all PF-xxx implementations including PF-001',
    parentElement: 'Product Framework',
    isDemo: true,
    lastUpdated: '2026-07-30',
  },
  {
    id: 'codex-007',
    title: 'Foundation Document Hierarchy',
    category: 'Foundation',
    content: `[DEMO — Prototype visualization only]

The KAVRIS Foundation Document Hierarchy defines the precedence and authority relationship between foundational institutional documents. The hierarchy: Constitution → Charter → Manifesto → Vision → Mission → Origin → Product Identity.

Higher-precedence documents govern lower-precedence documents. No lower-precedence document may contradict a higher-precedence document.

**Governance Note:** This entry is a prototype placeholder. Official content is reserved for approved KAVRIS source material.`,
    status: 'Working',
    authority: 'Foundation',
    version: 'v0.1',
    relatedAtlasNodes: ['node-foundation'],
    relatedCodexEntries: ['codex-001', 'codex-002'],
    frameworkRelationship: 'Establishes the authority basis for all PF items',
    parentElement: 'KAVRIS Foundation',
    isDemo: true,
    lastUpdated: '2026-08-01',
  },
];

export const foundationDocuments: FoundationDocument[] = [
  {
    id: 'found-001',
    title: 'Origin',
    type: 'Institutional Document',
    excerpt: 'Content reserved for approved KAVRIS source material.',
    status: 'Working',
    version: 'v0.1',
    isDemo: true,
  },
  {
    id: 'found-002',
    title: 'Vision',
    type: 'Institutional Document',
    excerpt: 'Content reserved for approved KAVRIS source material.',
    status: 'Working',
    version: 'v0.1',
    isDemo: true,
  },
  {
    id: 'found-003',
    title: 'Mission',
    type: 'Institutional Document',
    excerpt: 'Content reserved for approved KAVRIS source material.',
    status: 'Working',
    version: 'v0.1',
    isDemo: true,
  },
  {
    id: 'found-004',
    title: 'Manifesto',
    type: 'Institutional Document',
    excerpt: 'Content reserved for approved KAVRIS source material.',
    status: 'Working',
    version: 'v0.1',
    isDemo: true,
  },
  {
    id: 'found-005',
    title: 'Constitution',
    type: 'Institutional Document',
    excerpt: 'Content reserved for approved KAVRIS source material.',
    status: 'Working',
    version: 'v0.1',
    isDemo: true,
  },
  {
    id: 'found-006',
    title: 'Charter',
    type: 'Institutional Document',
    excerpt: 'Content reserved for approved KAVRIS source material.',
    status: 'Working',
    version: 'v0.1',
    isDemo: true,
  },
  {
    id: 'found-007',
    title: 'Product Identity',
    type: 'Institutional Document',
    excerpt: 'Content reserved for approved KAVRIS source material.',
    status: 'Working',
    version: 'v0.1',
    isDemo: true,
  },
];

export const knowledgeRecords = [
  {
    id: 'know-001',
    title: 'What is an Intelligence Ecosystem?',
    category: 'Codex',
    status: 'Working' as NodeStatus,
    authority: 'Codex' as NodeAuthority,
    tags: ['ecosystem', 'intelligence', 'architecture'],
    isDemo: true,
  },
  {
    id: 'know-002',
    title: 'Node Relationship Types',
    category: 'Atlas',
    status: 'Working' as NodeStatus,
    authority: 'Atlas' as NodeAuthority,
    tags: ['nodes', 'relationships', 'atlas'],
    isDemo: true,
  },
  {
    id: 'know-003',
    title: 'Authority vs. Status — Distinction',
    category: 'Codex',
    status: 'Working' as NodeStatus,
    authority: 'Codex' as NodeAuthority,
    tags: ['authority', 'status', 'governance'],
    isDemo: true,
  },
  {
    id: 'know-004',
    title: 'Product Framework Architecture Pattern',
    category: 'Product Framework',
    status: 'Draft' as NodeStatus,
    authority: 'Product Framework' as NodeAuthority,
    tags: ['product', 'framework', 'architecture'],
    isDemo: true,
  },
  {
    id: 'know-005',
    title: 'Foundation Document Precedence',
    category: 'Foundation',
    status: 'Working' as NodeStatus,
    authority: 'Foundation' as NodeAuthority,
    tags: ['foundation', 'documents', 'governance'],
    isDemo: true,
  },
  {
    id: 'know-006',
    title: 'Enterprise Edition Concept',
    category: 'Enterprise',
    status: 'Draft' as NodeStatus,
    authority: 'Product Framework' as NodeAuthority,
    tags: ['enterprise', 'pf-001', 'implementation'],
    isDemo: true,
  },
  {
    id: 'know-007',
    title: 'KAVRIS Status Lifecycle',
    category: 'Codex',
    status: 'Experimental' as NodeStatus,
    authority: 'Codex' as NodeAuthority,
    tags: ['status', 'lifecycle', 'governance'],
    isDemo: true,
  },
  {
    id: 'know-008',
    title: 'Atlas Layer Visualization Model',
    category: 'Atlas',
    status: 'Experimental' as NodeStatus,
    authority: 'Atlas' as NodeAuthority,
    tags: ['atlas', 'layers', 'visualization'],
    isDemo: true,
  },
];

export const searchableItems = [
  ...kavrisNodes.map(n => ({
    id: n.id,
    title: n.label,
    type: 'Atlas' as const,
    category: n.type,
    status: n.status,
    excerpt: n.shortDescription,
    route: '/atlas',
  })),
  ...codexEntries.map(e => ({
    id: e.id,
    title: e.title,
    type: 'Codex' as const,
    category: e.category,
    status: e.status,
    excerpt: e.content.substring(0, 100) + '...',
    route: '/codex',
  })),
  ...foundationDocuments.map(f => ({
    id: f.id,
    title: f.title,
    type: 'Foundation' as const,
    category: f.type,
    status: f.status,
    excerpt: f.excerpt,
    route: '/product-framework',
  })),
  ...knowledgeRecords.map(k => ({
    id: k.id,
    title: k.title,
    type: 'Knowledge' as const,
    category: k.category,
    status: k.status,
    excerpt: `[DEMO] Knowledge record — ${k.tags.join(', ')}`,
    route: '/codex',
  })),
];